import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';

/**
* AI:
* Status: 'in progress'
* Type: Service
* SubType: WidgetConfig
* Reason: Configured data provider for widget data fetching
*/

import {ActivatedRoute} from '@angular/router';
import {
    CnObjectModeViewBase
} from 'features/feature-prov/cn/shared/cn-object-mode-view.base';
import {ObjectIdentifier} from 'features/core/shared/basic/object-identifier';
import {ObjectIdentifierData} from 'features/core/shared/basic/object-identifier-data';
import {NavigationItem} from 'features/core/shared/dto/navigation/navigation-item';
import {WidgetData} from 'features/core/shared/widget/widget-data';
import {WidgetFactory} from 'features/core/shared/widget/service/widget.factory';
import {FilterCriteria} from 'features/core/shared/filter/api/filter.criteria';
import {Sorting} from 'features/core/shared/misc/sorting';
import {
    WidgetFunctionCallBackCreate,
    WidgetToolbarCreateInterface
} from 'features/core/shared/helper/widget-function-call-back-create';
import {MvsObjectNavigationService} from 'features/core/shared/navigation/mvs-object-navigation.service';
import {MvsObjectNavigationEntry} from 'features/core/shared/navigation/mvs-object-navigation-entry';
import {
    MvsObjectNavigationActionEnum
} from 'features/core/shared/navigation/mvs-object-navigation-action-enum';
import {cnStatusColorStyle, cnStatusSeverity, CnTagSeverity} from 'features/feature-prov/cn/shared/cn-status-color.helper';
import {CommissionModelDto} from 'features/feature-prov/cn/model/dto/entity/commission-model/commission-model.dto';
import {ObjectChangeInformation} from 'features/core/shared/logic/object-change-information';
import {
    CommissionModelStatusEnum
} from 'features/feature-prov/cn/model/protected-domain/enum/commission-model-status/commission-model-status.enum';

/**
* Shared base for the CommissionModel object-component views (FR-8).
*
* Builds the metadata-driven widgets for each left-nav section and lazily renders them:
*   cn.CommissionModel             Allgemein (master data; status / approvedBy shown in header)
*   cn.CommissionRule              Regeln       (FK commissionModel; row-click -> rule object page)
*   cn.CommissionModelAssignment   Zuordnungen  (FK commissionModel)
*   cn.CommissionLogicHook         LogicHooks   (FK commissionModel)
*
* Clicking a rule row opens the dedicated rule object page (CONFIG-015) — never inline.
* Child creation uses FK-defaulting on commissionModelDtoId (READ-ONLY, CONFIG-012).
* Status badge color comes from the enum uiColor (rendered by mvs-widget) / a neutral
* severity for the hand-built header tag — never a hardcoded business color.
*/
@Component({
    selector: 'mvs-cn-commission-model-object-base',
    templateUrl: './cn-commission-model-object-base-view.component.html',
    styleUrls: ['./cn-commission-model-object-base-view.component.scss'],
    standalone: false
})
export class CnCommissionModelObjectBaseViewComponent extends CnObjectModeViewBase
    implements OnInit, OnChanges, OnDestroy {

    @Input() objectIdentifier: ObjectIdentifier;
    @Input() name: string;
    @Input() override dto: CommissionModelDto;

    @Output() onNavigationItems: EventEmitter<NavigationItem[]> = new EventEmitter();

    /** Left-nav sections (built by the uiMode-specific view); declared here for G5.1 / G6.1. */
    navigationItems: NavigationItem[];

    busy: boolean;
    initialized: boolean;

    modelWidget: WidgetData;
    ruleWidget: WidgetData;
    assignmentWidget: WidgetData;
    logicHookWidget: WidgetData;

    constructor(
        protected activatedRoute: ActivatedRoute,
        protected navigationService: MvsObjectNavigationService
    ) {
        super(activatedRoute);
    }

    /**
    * Initialize the view: pick the default left-nav section, emit the nav items to the host
    * object page and build the section widgets. Driven by the uiMode-specific subclass from its
    * ngOnInit(); ngOnInit itself is inherited (OBMC route handling) and not overridden here.
    */
    initComponent(): void {
        if (this.navigationItems?.length) {
            this.activeNavigationItem = this.navigationItems[0];
        }
        this.handleNavigationItemsChange();
        this.refreshComponent();
    }

    refreshComponent(): void {
        if (!this.objectIdentifier?.objectId) {
            return;
        }
        const modelId = this.objectIdentifier.objectId;

        this.refreshModelWidget(modelId);
        this.refreshRuleWidget(modelId);
        this.refreshAssignmentWidget(modelId);
        this.refreshLogicHookWidget(modelId);

        this.initialized = true;
    }

    /**
    * AI: Allgemein — model master data form (metadata-driven).
    * @widget cn.commission.model.detail.widget (entity, list)
    */
    refreshModelWidget(modelId: number): void {
        this.modelWidget = WidgetFactory.createWidgetEntityData(
            'cn.commission.model.detail.widget',
            'Allgemein',
            'cn.CommissionModel',
            modelId
        );
    }

    /**
    * AI: Regeln — CommissionRule filtered by model, FK-defaulted, row-click opens rule page.
    * @widget cn.commission.model.rule.widget (entity, list)
    */
    refreshRuleWidget(modelId: number): void {
        this.ruleWidget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            'cn.commission.model.rule.widget',
            'Regeln',
            'cn.CommissionRule',
            [FilterCriteria.create('e.commissionModelId', FilterCriteria.cOperatorEqual, modelId)],
            // TM-1000001915 (B2): sort by real entity field orderPriority (e.priority does not exist)
            [new Sorting('e.orderPriority', true)]
        );
        this.ruleWidget.functionCallbacks = this.childCreateCallback('commissionModelDtoId', modelId);
    }

    /**
    * AI: Zuordnungen — CommissionModelAssignment filtered by model, FK-defaulted.
    * @widget cn.commission.model.assignment.widget (entity, list)
    */
    refreshAssignmentWidget(modelId: number): void {
        this.assignmentWidget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            'cn.commission.model.assignment.widget',
            'Zuordnungen',
            'cn.CommissionModelAssignment',
            [FilterCriteria.create('e.commissionModelId', FilterCriteria.cOperatorEqual, modelId)],
            // TM-1000001915 (B2): sort by real entity field orderPriority (e.priority does not exist)
            [new Sorting('e.orderPriority', true)]
        );
        this.assignmentWidget.functionCallbacks = this.childCreateCallback('commissionModelDtoId', modelId);
    }

    /**
    * AI: LogicHooks — CommissionLogicHook filtered by model, FK-defaulted.
    * @widget cn.commission.model.logic.hook.widget (entity, list)
    */
    refreshLogicHookWidget(modelId: number): void {
        this.logicHookWidget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            'cn.commission.model.logic.hook.widget',
            'LogicHooks',
            'cn.CommissionLogicHook',
            [FilterCriteria.create('e.commissionModelId', FilterCriteria.cOperatorEqual, modelId)],
            // TM-1000001915 (B2): sort by real entity field orderPriority (e.sortOrder does not exist)
            [new Sorting('e.orderPriority', true)]
        );
        this.logicHookWidget.functionCallbacks = this.childCreateCallback('commissionModelDtoId', modelId);
    }

    /**
    * Row-click handler for the rule table: opens the dedicated rule object page (CONFIG-015).
    */
    handleRuleSelect(object: ObjectIdentifierData | ObjectIdentifier): void {
        const data = object as ObjectIdentifierData;
        const ruleId = data?.objectId ?? data?.data?.['e.id'];
        if (!ruleId) {
            return;
        }
        const entry = MvsObjectNavigationEntry.createNavigationEntry(
            'cn.CommissionRule',
            ruleId,
            'object',
            '',
            '',
            '',
            MvsObjectNavigationActionEnum.edit
        );
        this.navigationService.navigateTo(entry, 'main');
    }

    /**
    * TM-1000001915 (B4): returns the German display label for a CommissionModelStatusEnum value
    * (was returning the raw enum key e.g. 'SCENARIO'). ACTIVE->Aktiv, SCENARIO->Szenario,
    * ARCHIVED->Archiviert.
    */
    statusLabel(status: CommissionModelStatusEnum | null | undefined): string {
        if (status == null) {
            return '';
        }
        switch (status) {
            case CommissionModelStatusEnum.ACTIVE:
                return 'Aktiv';
            case CommissionModelStatusEnum.SCENARIO:
                return 'Szenario';
            case CommissionModelStatusEnum.ARCHIVED:
                return 'Archiviert';
            default:
                return CommissionModelStatusEnum[status] ?? String(status);
        }
    }

    /**
    * Neutral severity for the status tag (enum uiColor is the source for widget badges).
    * TM-1000001915 (B4): looks up severity by the English enum key, not the German label,
    * so the shared cnStatusSeverity map keeps matching after statusLabel() was localized.
    */
    statusSeverity(status: CommissionModelStatusEnum | null | undefined): CnTagSeverity {
        const enumKey = status == null ? undefined : CommissionModelStatusEnum[status];
        return cnStatusSeverity(enumKey);
    }

    /**
    * TM-1000001915 (B17): inline theme-token color for the hand-built status tag so the badge
    * color matches its meaning (Aktiv=green, Szenario=blue, Archiviert=grey) instead of the
    * theme's `success`-severity blue. Looks up by the English enum key (same lookup as
    * statusSeverity) and returns var(--*-200)/var(--*-800) tokens — no HEX/RGB.
    */
    statusColorStyle(status: CommissionModelStatusEnum | null | undefined): { [style: string]: string } | undefined {
        const enumKey = status == null ? undefined : CommissionModelStatusEnum[status];
        return cnStatusColorStyle(enumKey);
    }

    /**
    * TM-1000001915 (B15): when the Allgemein widget saves the model, the widget emits
    * onChangedObject with the persisted DTO. Update the bound dto so the header status badge and
    * Freigabevermerk re-render live (was stale until a hard reload). Also refresh the dependent
    * section widgets so they reflect the new state.
    */
    handleModelChanged(event: ObjectChangeInformation): void {
        const after = event?.after as CommissionModelDto | undefined;
        if (after) {
            // Re-bind a fresh reference so change detection re-renders the status badge / header.
            this.dto = {...this.dto, ...after} as CommissionModelDto;
        }
        if (this.objectIdentifier?.objectId) {
            this.refreshComponent();
        }
    }

    protected childCreateCallback(fieldName: string, fieldValue: number) {
        const fields: WidgetToolbarCreateInterface[] = [{fieldName, fieldValue}];
        return WidgetFunctionCallBackCreate.widgetToolbarCreate(fields);
    }

    getNavigationItems(): NavigationItem[] {
        return this.navigationItems;
    }

    handleNavigationItemsChange(): void {
        this.onNavigationItems.emit(this.navigationItems);
    }

    ngOnChanges(changes: SimpleChanges): void {
        super.ngOnChanges(changes);
        if (!this.initialized) {
            return;
        }
        if (changes['objectIdentifier']) {
            this.refreshComponent();
        }
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
    }
}
